import type React from "react";
import { useEffect, useState } from "react";
import { useHistory, Link } from "react-router-dom";
import {
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Pagination,
  Chip,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
  Autocomplete, // Added Autocomplete import
} from "@mui/material";
import {
  ArrowBack,
  UploadFile,
  Description,
  Person,
  Work,
  Assignment,
  CalendarToday,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
   import {
  baseUrl,
  config,
  requestID,
  getUser,
  formatDate,
} from "../../../common/utils";
import { IonContent, IonPage } from "@ionic/react";
import { getAllEmployees } from "../../../api/LeaveService";
import FileUploader from "../../../components/leave/FileUploader";
import { issueDisciplinaryQuery } from "../../../api/disciplinaryService";

const IssueDisciplinaryQueryPage: React.FC = () => {
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Form state
  const [employeeId, setEmployeeId] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [department, setDepartment] = useState("");
  const [issueType, setIssueType] = useState("");
  const [issueDescription, setIssueDescription] = useState("");
  const [responseDeadline, setResponseDeadline] = useState<Date | null>(null);
  const [files, setFiles] = useState<File[]>([]);

  // UI state
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [employees, setEmployees] = useState<any[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const user = getUser()?.userData;
  const itemsPerPage = 5; // For employee pagination

  // Calculate paginated employees
  const paginatedEmployees = employees.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const resetForm = () => {
    setEmployeeId("");
    setEmployeeName("");
    setDepartment("");
    setIssueType("");
    setIssueDescription("");
    setResponseDeadline(null);
    setFiles([]);
    setError(null);
    setSuccess(null);
    setActiveStep(0);
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    // Validate inputs
    if (!responseDeadline) {
      setError("Please select a response deadline");
      setIsSubmitting(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("user_id", employeeId);
      formData.append("issued_by", user.user_id);
      formData.append("issue_type", issueType);
      formData.append("issue_description", issueDescription);
      formData.append("department", department);
      formData.append("status", "pending");
      formData.append("responseDate", formatDate(responseDeadline));
      formData.append("requestID", requestID());

      const result = await issueDisciplinaryQuery(formData);
      setSuccess("Disciplinary query issued successfully");
      console.log("Disciplinary query issued successfully:", result);
    } catch (err: any) {
      setError(err.message || "Failed to issue disciplinary query");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      setLoadingEmployees(true);
      try {
        const employeeData = await getAllEmployees();
        setEmployees(employeeData);
        console.log("Fetched employees:", employeeData);
      } catch (err) {
        console.error("Failed to fetch employees:", err);
        setError("Failed to load employee data. Please try again later.");
      } finally {
        setLoadingEmployees(false);
      }
    };

    fetchEmployees();
  }, []);

  const steps = [
    {
      label: "Employee Information",
      icon: <Person />,
    },
    {
      label: "Issue Details",
      icon: <Assignment />,
    },
    {
      label: "Review & Submit",
      icon: <Description />,
    },
  ];

  return (
    <IonPage className="pushDown">
      <IonContent>
        <Box sx={{ flexGrow: 1, p: isMobile ? 1 : 3 }}>
          <AppBar
            position="static"
            color="transparent"
            elevation={0}
            sx={{
              mb: 3,
              borderBottom: 1,
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="back"
                component={Link}
                to="/disciplinary"
                sx={{ mr: 2 }}
              >
                {/* <ArrowBack /> */}
              </IconButton>
              <Typography
                variant="h5"
                component="h1"
                sx={{ flexGrow: 1, fontWeight: 600 }}
              >
                Issue Disciplinary Query
              </Typography>
              <Chip label="New Query" color="primary" variant="outlined" />
            </Toolbar>
          </AppBar>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              <AlertTitle>Error</AlertTitle>
              {error}
            </Alert>
          )}

          <Dialog
            open={!!success}
            onClose={() => setSuccess(null)}
            aria-labelledby="success-dialog-title"
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle id="success-dialog-title">
              <Typography
                variant="h6"
                component="div"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Assignment color="success" sx={{ mr: 1 }} />
                Query Issued Successfully
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Alert severity="success" variant="outlined">
                <AlertTitle>Success</AlertTitle>
                {success}
              </Alert>
              <Typography variant="body2" sx={{ mt: 2 }}>
                The employee has been notified and will need to respond by the
                deadline.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => history.push("/viewDiscipline")}
                variant="outlined"
              >
                View All Queries
              </Button>
              <Button onClick={resetForm} color="primary" variant="contained">
                Issue Another Query
              </Button>
            </DialogActions>
          </Dialog>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{ p: 3, bgcolor: "grey.50", borderRadius: 2 }}
              >
                <Stepper activeStep={activeStep} orientation="vertical">
                  {steps.map((step, index) => (
                    <Step key={step.label}>
                      <StepLabel
                        StepIconComponent={() => (
                          <Avatar
                            sx={{
                              bgcolor:
                                activeStep === index
                                  ? "primary.main"
                                  : "grey.300",
                              width: 32,
                              height: 32,
                            }}
                          >
                            {step.icon}
                          </Avatar>
                        )}
                      >
                        <Typography variant="subtitle2">
                          {step.label}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        <Typography variant="caption" color="text.secondary">
                          {index === 0 && "Select the employee and department"}
                          {index === 1 && "Provide details about the issue"}
                          {index === 2 &&
                            "Review information before submitting"}
                        </Typography>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>

                {activeStep < steps.length - 1 && (
                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      fullWidth
                      disabled={
                        (activeStep === 0 && (!employeeId || !department)) ||
                        (activeStep === 1 && (!issueType || !issueDescription))
                      }
                    >
                      Continue
                    </Button>
                    {activeStep > 0 && (
                      <Button onClick={handleBack} sx={{ mt: 1 }} fullWidth>
                        Back
                      </Button>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardHeader
                  title={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {steps[activeStep].icon}
                      <Typography variant="h6" sx={{ ml: 1 }}>
                        {steps[activeStep].label}
                      </Typography>
                    </Box>
                  }
                  subheader={
                    activeStep === 2
                      ? "Confirm all details are correct before submitting"
                      : "Please fill in all required fields"
                  }
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    bgcolor: "primary.light",
                    color: "primary.contrastText",
                    "& .MuiCardHeader-subheader": {
                      color: "primary.contrastText",
                    },
                  }}
                />
                <CardContent sx={{ p: 4 }}>
                  {activeStep === 0 && (
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Autocomplete
                          id="employee-select"
                          options={employees}
                          loading={loadingEmployees}
                          getOptionLabel={(option) => option.fullName || ""}
                          value={
                            employees.find(
                              (emp) => emp.user_id === employeeId
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            if (newValue) {
                              setEmployeeId(newValue.user_id);
                              setEmployeeName(newValue.fullName);
                            } else {
                              setEmployeeId("");
                              setEmployeeName("");
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select Employee"
                              variant="outlined"
                              required
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingEmployees ? (
                                      <CircularProgress
                                        color="inherit"
                                        size={20}
                                      />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props} key={option.user_id}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Avatar
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    mr: 2,
                                    bgcolor: "primary.main",
                                  }}
                                >
                                  {option.fullName
                                    ? option.fullName.charAt(0)
                                    : "U"}
                                </Avatar>
                                <Box>
                                  <Typography variant="body1">
                                    {option.fullName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    ID: {option.user_id}
                                  </Typography>
                                </Box>
                              </Box>
                            </li>
                          )}
                          isOptionEqualToValue={(option, value) =>
                            option.user_id === value.user_id
                          }
                          noOptionsText="No employees found"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          id="employeeId"
                          label="Employee ID"
                          value={employeeId}
                          fullWidth
                          required
                          disabled
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FormControl fullWidth required>
                          <InputLabel id="department-label">
                            Department
                          </InputLabel>
                          <Select
                            labelId="department-label"
                            id="department"
                            value={department}
                            label="Department"
                            onChange={(e) =>
                              setDepartment(e.target.value as string)
                            }
                            startAdornment={
                              <Work color="action" sx={{ mr: 1 }} />
                            }
                          >
                            <MenuItem value="engineering">Engineering</MenuItem>
                            <MenuItem value="marketing">Marketing</MenuItem>
                            <MenuItem value="finance">Finance</MenuItem>
                            <MenuItem value="hr">Human Resources</MenuItem>
                            <MenuItem value="operations">Operations</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  )}

                  {activeStep === 1 && (
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <FormControl fullWidth required>
                          <InputLabel id="issue-type-label">
                            Issue Type
                          </InputLabel>
                          <Select
                            labelId="issue-type-label"
                            id="issueType"
                            value={issueType}
                            label="Issue Type"
                            onChange={(e) =>
                              setIssueType(e.target.value as string)
                            }
                          >
                            <MenuItem value="attendance">Attendance</MenuItem>
                            <MenuItem value="performance">Performance</MenuItem>
                            <MenuItem value="conduct">Conduct</MenuItem>
                            <MenuItem value="policy_violation">
                              Policy Violation
                            </MenuItem>
                            <MenuItem value="insubordination">
                              Insubordination
                            </MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          id="issueDescription"
                          label="Issue Description"
                          placeholder="Provide a detailed description of the issue..."
                          multiline
                          rows={6}
                          value={issueDescription}
                          onChange={(e) => setIssueDescription(e.target.value)}
                          fullWidth
                          required
                          variant="outlined"
                          helperText="Be specific about the issue, including dates, times, and any relevant details. This will be visible to the employee."
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <DatePicker
                          label="Response Deadline"
                          value={responseDeadline}
                          onChange={(newValue) => setResponseDeadline(newValue)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: true,
                              variant: "outlined",
                              helperText:
                                "Select a reasonable deadline for the employee to respond. Typically 48-72 hours from now.",
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <UploadFile sx={{ mr: 1 }} />
                          Supporting Documents (Optional)
                        </Typography>
                        <FileUploader
                          onFilesChange={setFiles}
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          maxSize={5}
                          multiple={true}
                          description="Upload any supporting evidence or documentation"
                        />
                      </Grid>
                    </Grid>
                  )}

                  {activeStep === 2 && (
                    <Box>
                      <Typography variant="h6" gutterBottom color="primary">
                        Review Query Details
                      </Typography>
                      <Divider sx={{ mb: 3 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Employee Name
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {employeeName || "Not specified"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Employee ID
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {employeeId || "Not specified"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Department
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {department || "Not specified"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Issue Type
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {issueType || "Not specified"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Response Deadline
                          </Typography>
                          <Typography
                            variant="body1"
                            gutterBottom
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <CalendarToday sx={{ mr: 1, fontSize: 20 }} />
                            {responseDeadline
                              ? formatDate(responseDeadline)
                              : "Not specified"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Issue Description
                          </Typography>
                          <Paper
                            variant="outlined"
                            sx={{ p: 2, mt: 1, bgcolor: "grey.50" }}
                          >
                            <Typography variant="body2">
                              {issueDescription || "No description provided"}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Attachments ({files.length})
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            {files.length > 0 ? (
                              files.map((file, index) => (
                                <Chip
                                  key={index}
                                  icon={<Description />}
                                  label={file.name}
                                  variant="outlined"
                                  sx={{ m: 0.5 }}
                                />
                              ))
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No files attached
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      </Grid>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mt: 4,
                        }}
                      >
                        <Button onClick={handleBack} variant="outlined">
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleSubmit}
                          disabled={isSubmitting}
                          startIcon={
                            isSubmitting ? <CircularProgress size={16} /> : null
                          }
                        >
                          {isSubmitting ? "Submitting..." : "Issue Query"}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default IssueDisciplinaryQueryPage;
